// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, Box, TablePagination, Chip, Button, CircularProgress
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { usePostApi } from "../data/hooks/api";
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { AccountSummary } from "../data/utils/types";
import LinearLoading from '../components/LinearLoading';
import { RiskFilterKey } from '../data/utils/types';
import { green, red, amber } from '@mui/material/colors';
import Tooltip from '@mui/material/Tooltip';
import { TOOLTIP_TEXT } from '../constants/constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
 
type CustomerHealthTableProps = {
    accountScope: "my-accounts" | "all-accounts";
    userEmail: string | null;
    searchQuery: string;
    riskIndicators: RiskFilterKey[];
    region: string[];
    product: string | null;
    abtTeam: string | null;
    healthStatus: string | null;
    page: number;
    rowsPerPage: number;
    onPageChange: (newPage: number) => void;
    onRowsPerPageChange: (newRowsPerPage: number) => void;
    onExport: () => void;
    exporting: boolean;
};

export default function CustomerHealthTable(props: CustomerHealthTableProps): JSX.Element {
    //State for pagination

    const navigate = useNavigate();
    const { data, loading, error, postApiData } = usePostApi<{ data: AccountSummary[], totalCount: number }>({
        url: "",  
        headers: { "accept": "application/json" }
    });
 
    useEffect(() => {
        const baseUrl = RESOURCE_SERVER_URLS[0] + `/customer-health/summary`;
        
        const payload = {
            offset: props.page * props.rowsPerPage,
            limit: props.rowsPerPage,
            email: (props.accountScope === "my-accounts" && props.userEmail) ? props.userEmail : "",
            phrase: (props.searchQuery && props.searchQuery.length >= 2) ? props.searchQuery : "",
            // Safety check for risks array
            risks: (Array.isArray(props.riskIndicators) && props.riskIndicators.length > 0) ? props.riskIndicators.join(',') : "",
            region: (Array.isArray(props.region) && props.region.length > 0) ? props.region : [],
            product: props.product || "",
            abtTeam: props.abtTeam || "",
            healthStatus: props.healthStatus || ""
        };

        postApiData(payload, baseUrl);
    }, [props.accountScope, props.userEmail, props.searchQuery, props.riskIndicators, props.region, props.product, props.abtTeam, props.healthStatus, props.page, props.rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        props.onPageChange(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        props.onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleAccountTableRowClick = (rowData: AccountSummary) => {
        const localIndex = accounts.findIndex(a => a.accountSysId === rowData.accountSysId);
        navigate(`/support/customer-health/account/${rowData.accountSysId}`, {
            state: {
                accountList: accounts.map(a => ({ accountSysId: a.accountSysId, accountName: a.accountName })),
                currentIndex: localIndex,
                absoluteIndex: props.page * props.rowsPerPage + localIndex,
                totalCount,
                filterPayload: {
                    email: (props.accountScope === "my-accounts" && props.userEmail) ? props.userEmail : "",
                    phrase: (props.searchQuery && props.searchQuery.length >= 2) ? props.searchQuery : "",
                    risks: (Array.isArray(props.riskIndicators) && props.riskIndicators.length > 0) ? props.riskIndicators.join(',') : "",
                    region: (Array.isArray(props.region) && props.region.length > 0) ? props.region : [],
                    product: props.product || "",
                    healthStatus: props.healthStatus || "",
                },
            }
        });
    };

    if (loading) return <LinearLoading />;

    // Update data access to use the wrapper object
    const accounts = data?.data || [];
    const totalCount = data?.totalCount || 0;
    return (
        <>
            <Box sx={{ my: 2 }}>
                <Typography variant="h6">Accounts Overview</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption">
                    {totalCount} accounts found
                    {props.healthStatus === 'at_risk' && ' · Filtered by: At Risk'}
                    {props.healthStatus === 'healthy' && ' · Filtered by: Healthy'}
                    {!props.healthStatus && ' at risk'}
                    .
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={props.exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
                    onClick={props.onExport}
                    disabled={props.exporting || totalCount === 0}
                    sx={{ textTransform: 'none', backgroundColor: '#e96900', color: 'white', '&:hover': { backgroundColor: '#c45a00', color: 'white' }, '&:disabled': { backgroundColor: '#f0c090', color: 'white' } }}
                >
                    {props.exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
            </Box>

            <Paper>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table stickyHeader aria-label="customer health summary table" sx={{ minWidth: 1200 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    rowSpan={2}
                                    sx={{
                                        verticalAlign: 'middle',
                                        whiteSpace: 'nowrap',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 4,
                                        backgroundColor: '#F8F9FA',
                                        '&.MuiTableCell-stickyHeader': { backgroundColor: '#F8F9FA', zIndex: 4 },
                                        borderBottom: '1px solid #E0E0E0',
                                        boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                                        py: 1.5, px: 2,
                                    }}
                                >
                                    Account Name
                                </TableCell>
                                <TableCell
                                    rowSpan={2}
                                    align="center"
                                    sx={{
                                        verticalAlign: 'middle',
                                        backgroundColor: '#F8F9FA',
                                        '&.MuiTableCell-stickyHeader': { backgroundColor: '#F8F9FA' },
                                        borderBottom: '1px solid #E0E0E0',
                                        py: 1.5, px: 2,
                                    }}
                                >
                                    <HeaderTooltip label="Health Status" text={TOOLTIP_TEXT.healthStatus} />
                                </TableCell>
                                <TableCell
                                    colSpan={6}
                                    align="center"
                                    sx={{
                                        backgroundColor: '#F8F9FA',
                                        '&.MuiTableCell-stickyHeader': { backgroundColor: '#F8F9FA' },
                                        color: '#888',
                                        fontWeight: 500,
                                        fontSize: '0.72rem',
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #E0E0E0',
                                        py: 0.75, px: 2,
                                    }}
                                >
                                    Risk Indicators
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                {[
                                    { label: 'Has Gone Live', tip: TOOLTIP_TEXT.goLive },
                                    { label: 'Support Activity (Last 6 Mo)', tip: TOOLTIP_TEXT.support },
                                    { label: 'Using EOL Products', tip: TOOLTIP_TEXT.eol },
                                    { label: 'Abandoned Migrations', tip: TOOLTIP_TEXT.abandoned },
                                    { label: 'Migration Delays', tip: TOOLTIP_TEXT.delays },
                                    { label: 'Escalations (Last 3 Mo)', tip: TOOLTIP_TEXT.escalations },
                                ].map(({ label, tip }) => (
                                    <TableCell
                                        key={label}
                                        align="center"
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            backgroundColor: '#F8F9FA',
                                            '&.MuiTableCell-stickyHeader': { backgroundColor: '#F8F9FA' },
                                            borderBottom: '1px solid #E0E0E0',
                                            py: 1.5, px: 3,
                                        }}
                                    >
                                        <HeaderTooltip label={label} text={tip} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {error ? (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                            <ErrorOutlineIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
                                            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
                                                Something happened while fetching the accounts.
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Please refresh the page and try again later.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                accounts?.map((acc) => (
                                    <TableRow
                                        key={acc.accountSysId}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#F5F5F5' },
                                        }}
                                        onClick={() => handleAccountTableRowClick(acc)}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            sx={{
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1,
                                                backgroundColor: '#fff',
                                                boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <Box component="span" sx={{
                                                color: '#e96900',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}>
                                                {acc.accountName || `Account ID: ${acc.accountSysId.substring(0, 8)}...`}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <ReviewStatusBadge status={acc.healthStatus} />
                                        </TableCell>

                                        <TableCell align="center" sx={{ px: 3 }}><StatusIndicator isRisk={acc.hasNoGoLive.isRisk} state={acc.hasNoGoLive.state} /></TableCell>
                                        <TableCell align="center" sx={{ px: 3 }}><StatusIndicator isRisk={acc.noSupportCases6mo} /></TableCell>
                                        <TableCell align="center" sx={{ px: 3 }}><StatusIndicator isRisk={acc.hasEolProduct} riskShowsYes /></TableCell>
                                        <TableCell align="center" sx={{ px: 3 }}><StatusIndicator isRisk={acc.hasAbandonedMigrations} riskShowsYes /></TableCell>
                                        <TableCell align="center" sx={{ px: 3 }}><StatusIndicator isRisk={acc.hasMigrationDelays} riskShowsYes /></TableCell>
                                        <TableCell align="center" sx={{ px: 3 }}><StatusIndicator isRisk={acc.hasRecentEscalations} riskShowsYes /></TableCell>

                                    </TableRow>
                            )))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]} 
                    component="div"
                    count={totalCount}  
                    rowsPerPage={props.rowsPerPage}
                    page={props.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    showFirstButton
                    showLastButton
                    sx={{
                        borderTop: '1px solid #e0e0e0', 
                        '& .MuiTablePagination-toolbar': {
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingLeft: '16px',
                        },
                        '& .MuiTablePagination-spacer': {
                            display: 'none',  
                        },
                        '& .MuiTablePagination-selectLabel': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#000000',
                            margin: '0 8px 0 0',
                        },
                        '& .MuiTablePagination-select': {
                            fontSize: '0.875rem',
                            margin: '0 6px 0 2px',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            minWidth: '50px',
                        },
                        '& .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#000000',
                            margin: '0 12px 0 0',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: '4px',
                        }
                    }}
                />
                {accounts?.length === 0 && !loading && (  
                    <Typography sx={{ p: 3, textAlign: 'center' }}>No accounts found matching the criteria.</Typography>
                )}
            </Paper>
        </>
    );
}

const ReviewStatusBadge = ({ status }: { status?: string }) => {
    const chipSx = {
        fontWeight: 600, fontSize: '0.8rem',
        borderRadius: '16px', backgroundColor: 'transparent',
        border: '2px solid',
        minWidth: 120, height: 28,
        '& .MuiChip-label': { px: 1.5, textAlign: 'center', width: '100%' },
    };
    if (!status || status === 'to_be_reviewed') {
        return <Chip label="To Be Reviewed" sx={{ ...chipSx, borderColor: '#E65100', color: '#E65100', '&:hover': { backgroundColor: 'transparent' } }} />;
    }
    if (status === 'at_risk') {
        return <Chip label="At Risk" sx={{ ...chipSx, borderColor: '#C62828', color: '#C62828', '&:hover': { backgroundColor: 'transparent' } }} />;
    }
    if (status === 'healthy') {
        return <Chip label="Healthy" sx={{ ...chipSx, borderColor: '#2E7D32', color: '#2E7D32', '&:hover': { backgroundColor: 'transparent' } }} />;
    }
    return null;
};

// By default the letter answers the positively-phrased column label (e.g. "Has Gone
// Live"): a risk shows "N". For columns phrased as the risk condition itself (e.g.
// "Using EOL Products"), pass riskShowsYes so a risk shows "Y" instead - the color
// (red on risk, green otherwise) never changes, only which letter reflects that state.
const StatusIndicator = ({ isRisk, state, riskShowsYes = false }: { isRisk: boolean, state?: string, riskShowsYes?: boolean }) => {
    if (!isRisk && state === 'pending') {
        return (
            <Tooltip title="Not Live Yet" arrow placement="top">
                <Chip
                    label="P"
                    size="small"
                    sx={{ backgroundColor: amber[600], color: '#fff', fontWeight: 700, minWidth: 32 }}
                />
            </Tooltip>
        );
    }

    if (state === 'failure' || isRisk === true) {
        return (
            <Chip
                label={riskShowsYes ? 'Y' : 'N'}
                size="small"
                sx={{ backgroundColor: red[500], color: '#fff', fontWeight: 700, minWidth: 32 }}
            />
        );
    }

    return (
        <Chip
            label={riskShowsYes ? 'N' : 'Y'}
            size="small"
            sx={{ backgroundColor: green[500], color: '#fff', fontWeight: 700, minWidth: 32 }}
        />
    );
};

const HeaderTooltip = ({ label, text }: { label: string, text: string }) => (
    <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}
    >
        <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#424242' }}>
            {label}
        </Typography>
        <Tooltip title={text} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9E9E9E', cursor: 'help', '&:hover': { color: '#424242' } }} />
        </Tooltip>
    </Box>
);
